import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/axios.config';
import { toast } from 'react-hot-toast';
import Loading from '../Shared/Loading';
import { baseUrl } from '../../utils/url';
import useAdmin from '../../hooks/useAdmin';
import { Upload, Image } from 'lucide-react';

const LogoManagement = () => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [adminData, adminLoading] = useAdmin();
  const isAdmin = adminData?.success;

  // Fetch current logo (must be called before any conditional returns)
  const { data, isLoading, error } = useQuery({
    queryKey: ['logoSetting'],
    queryFn: async () => {
      const response = await api.get('/setting/logo');
      return response.data.data;
    },
    retry: false, // Don't retry on failure
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Set preview URL when data changes (replaces deprecated onSuccess)
  useEffect(() => {
    if (data?.imageUrl) {
      setPreviewUrl(`${baseUrl}${data.imageUrl}`);
    } else {
      setPreviewUrl(null); // No logo exists yet
    }
  }, [data]);

  // Mutation for updating logo (must be called before any conditional returns)
  const updateLogoMutation = useMutation({
    mutationFn: async (formData) => {
      return api.put('/setting/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: () => {
      toast.success('লোগো সফলভাবে আপডেট হয়েছে!');
      setSelectedFile(null);
      queryClient.invalidateQueries(['logoSetting']);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'লোগো আপডেট করতে সমস্যা হয়েছে।');
    },
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setSelectedFile(null);
      setPreviewUrl(data?.imageUrl ? `${baseUrl}${data.imageUrl}` : null);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedFile) {
      toast.error('আপলোড করার জন্য একটি ফাইল নির্বাচন করুন।');
      return;
    }

    const formData = new FormData();
    formData.append('logo', selectedFile);
    updateLogoMutation.mutate(formData);
  };

  // Check admin access (after all hooks are called)
  if (adminLoading) return <Loading />;
  if (!isAdmin) {
    return (
      <div className="text-center py-10 text-red-500">
        <p>অ্যাক্সেস নিষেধ। শুধুমাত্র অ্যাডমিনরা লোগো পরিবর্তন করতে পারবেন।</p>
      </div>
    );
  }

  if (isLoading) return <Loading />;
  
  // Don't show error if it's just that no logo exists yet
  if (error && error.response?.status !== 404) {
    return <div className="text-red-500">Error: {error.message}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <Image className="mr-2" size={28} />
            লোগো ব্যবস্থাপনা
          </h2>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Current Logo Display */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">বর্তমান লোগো:</h3>
              <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed border-gray-300 text-center">
                {previewUrl ? (
                  <img 
                    src={previewUrl} 
                    alt="Current Logo" 
                    className="max-w-full h-auto max-h-64 mx-auto rounded-md shadow-sm" 
                  />
                ) : (
                  <div className="py-12">
                    <Image className="mx-auto text-gray-400 mb-2" size={48} />
                    <p className="text-gray-500">কোন লোগো নির্ধারিত নেই।</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Form */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">নতুন লোগো আপলোড:</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-gray-50 p-6 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <Upload className="mx-auto text-gray-400 mb-2" size={48} />
                    <label htmlFor="logo-upload" className="cursor-pointer">
                      <span className="text-blue-600 font-medium hover:text-blue-700">
                        ফাইল নির্বাচন করুন
                      </span>
                      <span className="text-gray-600"> অথবা এখানে টেনে আনুন</span>
                    </label>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    PNG, JPG, GIF পর্যন্ত 5MB
                  </p>
                </div>

                {selectedFile && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center text-green-700">
                      <Image className="mr-2" size={16} />
                      <span className="text-sm font-medium">{selectedFile.name}</span>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className={`btn w-full ${
                    selectedFile && !updateLogoMutation.isLoading
                      ? 'btn-primary'
                      : 'btn-disabled'
                  }`}
                  disabled={!selectedFile || updateLogoMutation.isLoading}
                >
                  {updateLogoMutation.isLoading ? (
                    <>
                      <span className="loading loading-spinner loading-sm mr-2"></span>
                      আপলোড হচ্ছে...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2" size={16} />
                      লোগো আপলোড করুন
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoManagement;
